import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  saveMerchantCarts,
  getMerchantCarts,
  type MerchantCart,
  type StoredCartItem,
} from '@paulownia/shared/lib/storage';

interface CartContextValue {
  carts: MerchantCart[];
  getCartForMerchant: (merchantId: string) => MerchantCart | null;
  setCartItems: (
    merchantId: string,
    merchantSlug: string,
    merchantName: string,
    marketId: string,
    marketCity: string,
    marketDayOfWeek: number,
    items: StoredCartItem[]
  ) => void;
  clearCartForMerchant: (merchantId: string) => void;
  clearAllCarts: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [carts, setCarts] = useState<MerchantCart[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Load carts from storage on mount
  useEffect(() => {
    getMerchantCarts()
      .then((stored) => {
        setCarts(stored);
      })
      .catch((error) => {
        console.error('Error loading merchant carts:', error);
      })
      .finally(() => {
        setInitialized(true);
      });
  }, []);

  // Persist carts to storage whenever they change (after initial load)
  useEffect(() => {
    if (initialized) {
      saveMerchantCarts(carts);
    }
  }, [carts, initialized]);

  const getCartForMerchant = useCallback(
    (merchantId: string): MerchantCart | null => {
      return carts.find((c) => c.merchantId === merchantId) || null;
    },
    [carts]
  );

  const setCartItems = useCallback(
    (
      merchantId: string,
      merchantSlug: string,
      merchantName: string,
      marketId: string,
      marketCity: string,
      marketDayOfWeek: number,
      items: StoredCartItem[]
    ) => {
      setCarts((prev) => {
        if (items.length === 0) {
          return prev.filter((c) => c.merchantId !== merchantId);
        }

        const existing = prev.find((c) => c.merchantId === merchantId);
        if (existing) {
          return prev.map((c) =>
            c.merchantId === merchantId
              ? { ...c, marketId, marketCity, marketDayOfWeek, items, merchantSlug, merchantName }
              : c
          );
        }

        return [...prev, { merchantId, merchantSlug, merchantName, marketId, marketCity, marketDayOfWeek, items }];
      });
    },
    []
  );

  const clearCartForMerchant = useCallback((merchantId: string) => {
    setCarts((prev) => prev.filter((c) => c.merchantId !== merchantId));
  }, []);

  const clearAllCarts = useCallback(() => {
    setCarts([]);
  }, []);

  return (
    <CartContext.Provider
      value={{ carts, getCartForMerchant, setCartItems, clearCartForMerchant, clearAllCarts }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useMerchantCarts() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useMerchantCarts must be used within a CartProvider');
  }
  return context;
}
